import { PriceSummary } from 'shared/payments/types';
import * as fmt from 'shared/payments/fmt';
import { useIntl } from 'shared/intl/use-intl';
import { CurrencyFlag } from './currency-flag';

type ToggleCoupon = {
  id: string;
  currency?: string;
  amountOff?: number;
};

type Props = {
  prices: PriceSummary[];
  activePriceId: string;
  slug?: string;
  coupon?: ToggleCoupon;
  testModeToken?: string;
};

/**
 * Whether this coupon survives the jump to that price. An amount-off coupon
 * carries its own currency and Stripe refuses it against a price in another
 * one, so carrying it across would hand the buyer a checkout that fails at the
 * last step. A percentage coupon has no currency and travels fine.
 */
function couponAppliesTo(price: PriceSummary, coupon?: ToggleCoupon): boolean {
  if (!coupon) {
    return false;
  }
  if (!coupon.amountOff) {
    return true;
  }
  // Coupons imported from Stripe keep Stripe's lowercase code, while prices
  // are stored uppercase — comparing them as-is would drop every imported
  // coupon, including on a link to the same currency.
  return (
    !coupon.currency || coupon.currency.toUpperCase() === (price.currency || '').toUpperCase()
  );
}

function buildUrl(
  slug: string,
  priceId: string,
  couponId?: string,
  testModeToken?: string,
): string {
  const params = new URLSearchParams({ price: priceId });
  if (couponId) {
    params.set('coupon', couponId);
  }
  if (testModeToken) {
    params.set('campaign_test', testModeToken);
  }
  return `/c/${slug}?${params}`;
}

function formatLabel(
  p: PriceSummary,
  intl: ReturnType<typeof useIntl>,
  explicitCurrency: boolean,
): string {
  const amount = fmt.price({ ...(p as any), explicitCurrency });
  if (p.paymentType === 'paid' || !p.interval) {
    return amount;
  }
  const suffix = fmt.priceSuffix({ item: p as any, compact: true, intl });
  return suffix ? `${amount} ${suffix}` : amount;
}

function formatDetail(p: PriceSummary): string {
  if (p.paymentType === 'paid' || !p.interval) {
    return 'One payment';
  }
  if (p.paymentType === 'paymentplan' && p.numPayments) {
    return 'Payment plan';
  }
  return 'Subscription';
}

/**
 * The saving from paying once instead of in instalments, within one currency.
 * Scoped to the active price's currency: on a campaign offering both, picking
 * the first of each kind could pair a US one-time with a Canadian plan, and
 * the badge would vanish for both currencies even though each has a real pair.
 */
function calcSavings(
  prices: PriceSummary[],
  currency?: string,
): { priceId: string; amount: number } | undefined {
  const sameCurrency = prices.filter((p) => p.currency === currency);
  const oneTime = sameCurrency.find((p) => p.paymentType === 'paid');
  const plan = sameCurrency.find((p) => p.paymentType === 'paymentplan' && p.numPayments);
  if (!oneTime || !plan || !plan.numPayments) {
    return undefined;
  }
  const planTotal = plan.priceInCents * plan.numPayments;
  const savings = planTotal - oneTime.priceInCents;
  if (savings > 0) {
    return { priceId: oneTime.id, amount: savings };
  }
  return undefined;
}

/**
 * True when the only thing separating the options is currency: one price per
 * currency. Then the choice is "which money", not "which plan", and a small
 * currency switch says that better than price boxes. The amount itself shows
 * in the checkout form below.
 */
function isCurrencyChoice(prices: PriceSummary[]): boolean {
  const currencies = new Set(prices.map((p) => (p.currency || '').toUpperCase()));
  return currencies.size === prices.length;
}

const currencyNames: Record<string, string> = {
  USD: 'US dollars',
  CAD: 'Canadian dollars',
};

function CurrencySwitch({
  prices,
  activePriceId,
  slug,
  coupon,
  testModeToken,
}: Props & { slug: string }) {
  return (
    <div class="currency-switch">
      <span class="currency-switch__label" id="currency-switch-label">
        Pay in
      </span>
      <div class="currency-switch__options" role="group" aria-labelledby="currency-switch-label">
        {prices.map((p) => {
          const code = (p.currency || '').toUpperCase();
          const content = (
            <>
              <CurrencyFlag currency={code} />
              {code}
            </>
          );
          const name = currencyNames[code] || code;
          if (p.id === activePriceId) {
            return (
              <span
                key={p.id}
                class="currency-switch__option currency-switch__option--active"
                aria-current="true"
                aria-label={name}
              >
                {content}
              </span>
            );
          }
          return (
            <a
              key={p.id}
              href={buildUrl(
                slug,
                p.id,
                couponAppliesTo(p, coupon) ? coupon?.id : undefined,
                testModeToken,
              )}
              class="currency-switch__option"
              aria-label={`Pay in ${name}`}
            >
              {content}
            </a>
          );
        })}
      </div>
    </div>
  );
}

export function PriceToggle({ prices, activePriceId, slug, coupon, testModeToken }: Props) {
  const intl = useIntl();
  if (prices.length < 2 || !slug) {
    return undefined;
  }

  if (isCurrencyChoice(prices)) {
    return (
      <CurrencySwitch
        prices={prices}
        activePriceId={activePriceId}
        slug={slug}
        coupon={coupon}
        testModeToken={testModeToken}
      />
    );
  }

  const activeCurrency = prices.find((p) => p.id === activePriceId)?.currency;
  const savings = calcSavings(prices, activeCurrency);
  // Two bare "$" prices side by side say nothing about which is which.
  const explicitCurrency = new Set(prices.map((p) => p.currency)).size > 1;

  return (
    <div class="price-toggle">
      {prices.map((p) => {
        const isActive = p.id === activePriceId;
        const savingsForThis =
          savings && savings.priceId === p.id
            ? fmt.price({
                priceInCents: savings.amount,
                currency: p.currency as any,
                explicitCurrency,
              })
            : undefined;

        if (isActive) {
          return (
            <div key={p.id} class="price-toggle__option price-toggle__option--active">
              <span class="price-toggle__label">{formatLabel(p, intl, explicitCurrency)}</span>
              <span class="price-toggle__detail">{formatDetail(p)}</span>
              {savingsForThis && <span class="price-toggle__savings">Save {savingsForThis}</span>}
            </div>
          );
        }

        return (
          <a
            key={p.id}
            href={buildUrl(
              slug,
              p.id,
              couponAppliesTo(p, coupon) ? coupon?.id : undefined,
              testModeToken,
            )}
            class="price-toggle__option"
          >
            <span class="price-toggle__label">{formatLabel(p, intl, explicitCurrency)}</span>
            <span class="price-toggle__detail">{formatDetail(p)}</span>
            {savingsForThis && <span class="price-toggle__savings">Save {savingsForThis}</span>}
          </a>
        );
      })}
    </div>
  );
}
